import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AwsSdkService } from 'src/app/shared/services/aws-sdk.service';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { CookieService } from 'ngx-cookie-service';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { SupportTicketSystemService } from '../support-ticket-system.service';
import {
  EmailPreviewData,
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
  TicketFormValues
} from '../support-ticket-system.models';

@Component({
  selector: 'app-buyer-support-ticket-system',
  templateUrl: './buyer-support-ticket-system.component.html',
  styleUrls: ['./buyer-support-ticket-system.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class BuyerSupportTicketSystemComponent implements OnInit {
  private readonly defaultDisplayName = 'Brenda Buyer';
  private readonly defaultAccountLabel = 'Buyer Account';
  private readonly defaultEmail = 'brenda.buyer@example.com';

  displayName = this.defaultDisplayName;
  accountLabel = this.defaultAccountLabel;
  allowTicketView = true;
  emailEntry = '';
  buyerName = '';
  buyerId: number | null = null;
  buyerAccountName = '';
  buyerAccountEmails: string[] = [];
  validationMessages: string[] = [];
  showValidationModal = false;
  showSuccessModal = false;
  successMessage = '';

  stats = { total: 0, open: 0, inProgress: 0, resolved: 0 };
  filterStatus = 'all';
  showCreateModal = false;
  showDetailsModal = false;
  showEmailModal = false;
  showStatusModal = false;
  dragActive = false;

  tickets: SupportTicket[] = [];
  searchTerm = '';
  selectedTicket: SupportTicket | null = null;
  emailPreview!: EmailPreviewData;
  uploadedFiles: { file: File; name: string; size: number; displaySize: string }[] = [];
  newTicket!: TicketFormValues;
  statusUpdateOptions: string[] = [];
  pendingStatusTicket: SupportTicket | null = null;
  statusUpdateSelection: SupportTicketStatus | '' = '';

  constructor(
    private awsSdkService: AwsSdkService,
    private webApiService: WebApiService,
    private cookieService: CookieService,
    private fullLoaderService: FullLoaderService,
    private supportTicketService: SupportTicketSystemService
  ) {
    this.tickets = this.buildTickets();
    this.newTicket = this.buildNewTicket();
    this.emailEntry = this.newTicket.emails.join(', ');
    const idFromCookie = this.cookieService.get('userId');
    this.buyerId = idFromCookie ? Number(idFromCookie) : null;
    this.buyerName = '';
  }

  ngOnInit(): void {
    this.loadBuyerTickets();
  }

  get userInitials(): string {
    return this.displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  getStatusClass(status: SupportTicketStatus): string {
    switch (status) {
      case 'Open':
        return 'status-open';
      case 'In Progress':
        return 'status-in-progress';
      case 'Resolved':
        return 'status-resolved';
      case 'Closed':
        return 'status-closed';
      default:
        return '';
    }
  }

  getPriorityClass(priority: SupportTicketPriority): string {
    switch (priority) {
      case 'High':
        return 'priority-high';
      case 'Medium':
        return 'priority-medium';
      case 'Low':
        return 'priority-low';
      case 'Urgent':
        return 'priority-urgent';
      default:
        return '';
    }
  }

  openCreateTicket(): void {
    this.newTicket = this.buildNewTicket();
    this.emailEntry = this.newTicket.emails.join(', ');
    this.buyerName = this.buyerAccountName || this.buyerName || this.displayName;
    this.uploadedFiles = [];
    this.showValidationModal = false;
    this.showSuccessModal = false;
    this.showCreateModal = true;
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showDetailsModal = false;
    this.showEmailModal = false;
    this.showStatusModal = false;
    this.pendingStatusTicket = null;
    this.statusUpdateSelection = '';
  }

  printEmailPreview(): void {
    const preview = document.getElementById('buyerEmailPreview');
    if (!preview) {
      console.warn('Email preview not found for printing.');
      return;
    }

    const ticketRef = this.selectedTicket?.ticketRefNumber || this.selectedTicket?.id || 'Ticket';
    const title = `Buyer_Support_Ticket_System_${ticketRef}.pdf`;
    const printMarkup = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1f2d3d; }
            .email-preview { max-width: 760px; margin: 0 auto; }
            .email-header { margin-bottom: 16px; }
            .email-to { margin-bottom: 6px; font-size: 14px; }
            .email-body h4 { margin: 16px 0 8px; }
            .email-body p, .email-body li { font-size: 14px; line-height: 1.5; }
            .email-body ul, .email-body ol { padding-left: 20px; }
          </style>
        </head>
        <body>
          ${preview.innerHTML}
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentWindow?.document;
    if (!frameDoc || !iframe.contentWindow) {
      console.warn('Unable to access print frame.');
      document.body.removeChild(iframe);
      return;
    }

    frameDoc.open();
    frameDoc.write(printMarkup);
    frameDoc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 500);
  }

  setFilter(filter: string): void {
    this.filterStatus = filter;
    const status = this.mapFilterToStatus(filter);
    this.loadBuyerTickets(status);
  }

  get filteredTickets(): SupportTicket[] {
    if (!this.searchTerm) {
      return this.tickets;
    }
    const term = this.searchTerm.toLowerCase();
    return this.tickets.filter((t) => {
      const matchesId = ((t.ticketRefNumber || t.id || '')).toLowerCase().includes(term);
      const matchesCategory = (t.category || '').toLowerCase().includes(term);
      const matchesBuyer = (t.buyerName || '').toLowerCase().includes(term);
      const matchesPriority = (t.priority || '').toLowerCase().includes(term);
      return matchesId || matchesCategory || matchesBuyer || matchesPriority;
    });
  }

  openTicketDetails(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.showDetailsModal = true;
  }

  openTicketEmail(ticket: SupportTicket): void {
    this.emailPreview = this.buildEmailPreviewFromTicket(ticket);
    this.showEmailModal = true;
  }

  openStatusModal(ticket: SupportTicket): void {
    this.pendingStatusTicket = ticket;
    this.statusUpdateOptions = this.getStatusUpdateOptions(ticket.status);
    this.statusUpdateSelection = (this.statusUpdateOptions[0] as SupportTicketStatus) || '';
    this.showStatusModal = true;
  }

  async submitStatusUpdate(newStatus: string | SupportTicketStatus | ''): Promise<void> {
    const resolvedStatus = newStatus || this.statusUpdateSelection;
    const nextStatus = this.validateStatus(resolvedStatus);
    if (!this.pendingStatusTicket || !nextStatus) {
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      this.statusUpdateSelection = '';
      return;
    }
    this.fullLoaderService.showLoader('Updating ticket status...');
    try {
      await this.supportTicketService.updateTicketStatusByBuyer({
        ticketId: (this.pendingStatusTicket.ticketId ?? this.pendingStatusTicket.id) as string,
        status: nextStatus
      });
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      this.statusUpdateSelection = '';
      await this.loadBuyerTickets(this.mapFilterToStatus(this.filterStatus));
    } catch (err) {
      console.error('Failed to update buyer ticket status', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  async downloadTicketDocuments(ticket?: SupportTicket | string): Promise<void> {
    const targetTicket =
      typeof ticket === 'string'
        ? this.tickets.find((t) => t.id === ticket) || this.selectedTicket
        : ticket || this.selectedTicket;

    if (!targetTicket || !targetTicket.attachments?.length) {
      console.warn('No attachments available to download for this ticket.');
      return;
    }

    this.fullLoaderService.showLoader('Downloading attachments...');
    try {
      for (const attachment of targetTicket.attachments) {
        await this.triggerAttachmentDownload(attachment);
      }
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  async downloadAttachment(name: string): Promise<void> {
    const ticket = this.selectedTicket;
    if (!ticket || !ticket.attachments?.length) {
      console.warn('No attachments available to download.');
      return;
    }
    const attachment = ticket.attachments.find((a) => a.name === name) || ticket.attachments[0];
    await this.triggerAttachmentDownload(attachment);
  }

  private async triggerAttachmentDownload(attachment: { link?: string; name?: string }): Promise<void> {
    if (!attachment?.link) {
      console.warn(`No link found for attachment: ${attachment?.name || 'Attachment'}`);
      return;
    }

    try {
      // slight delay between downloads to avoid browser blocking multiple rapid clicks
      await new Promise((resolve) => setTimeout(resolve, 50));
      const response = await fetch(attachment.link);
      if (!response.ok) {
        throw new Error(`Failed to fetch attachment: ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = attachment.name || 'attachment';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download attachment', attachment.name, err);
    }
  }

  triggerFileBrowse(input: HTMLInputElement): void {
    input.click();
  }

  async submitTicket(): Promise<void> {
    this.syncEmails();
    const missing: string[] = [];
    if (!this.buyerName.trim()) missing.push('Buyer Name');
    if (!this.newTicket.emails.length) missing.push('Email(s)');
    if (!this.newTicket.subject) missing.push('Subject');
    if (!this.newTicket.category) missing.push('Category');
    if (!this.newTicket.description) missing.push('Description');

    if (missing.length) {
      this.validationMessages = missing.map((m) => `${m} is required`);
      this.showValidationModal = true;
      return;
    }

    this.fullLoaderService.showLoader('Submitting support ticket...');
    try {
      const documentDetails = await this.uploadAttachmentsToS3();

      const backendPayload = {
        buyerId: this.buyerId,
        buyerName: this.buyerAccountName || this.buyerName,
        emails: this.newTicket.emails,
        subject: this.newTicket.subject,
        category: this.newTicket.category,
        priority: this.newTicket.priority,
        description: this.newTicket.description,
        attachments: documentDetails.map((doc) => ({
          documentName: doc.documentName,
          documentType: doc.documentType,
          documentFileS3Path: doc.s3path
        }))
      };

      const response = await this.supportTicketService.createBuyerTicket(backendPayload);
      const respTicket = response?.ticket || response || null;
      const respAttachments = response?.attachments || [];

      const ticket: SupportTicket = {
        id: respTicket?.ticketRefNumber || respTicket?.id || this.generateTicketId(),
        ticketRefNumber: respTicket?.ticketRefNumber || respTicket?.id,
        subject: this.newTicket.subject,
        category: this.getCategoryLabel(this.newTicket.category),
        status: 'Open',
        priority: this.formatPriority(this.newTicket.priority),
        created: new Date().toISOString().split('T')[0],
        submitter: `${this.buyerAccountName || this.buyerName || this.displayName} (${this.newTicket.emails.join(', ')}) - ${this.accountLabel}`,
        buyerName: this.buyerAccountName || this.buyerName,
        emails: [...this.newTicket.emails],
        contactEmail: this.newTicket.emails.join(', '),
        description: this.newTicket.description,
        attachments: (respAttachments.length ? respAttachments : documentDetails).map((doc: any) => ({
          name: doc.documentName || doc.name,
          size: '',
          icon: 'fas fa-file',
          link: doc.documentFileS3Path || doc.s3path || doc.url || ''
        }))
      };

      this.tickets = [ticket, ...this.tickets];
      this.selectedTicket = ticket;
      this.emailPreview = this.buildEmailPreviewFromTicket(ticket, this.newTicket.emails[0]);

      this.successMessage =
        'Ticket created successfully! An email has been sent to MPs@varuna-sentinel.com with all details and attachments.';
      this.showSuccessModal = true;

      this.showCreateModal = false;
      this.showEmailModal = true;
      this.uploadedFiles = [];
      this.newTicket = this.buildNewTicket();
      this.emailEntry = this.newTicket.emails.join(', ');
      this.buyerName = this.buyerAccountName || this.buyerName || this.displayName;
      await this.loadBuyerTickets(this.mapFilterToStatus(this.filterStatus));
    } catch (err) {
      console.error('Failed to submit support ticket', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(): void {
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFileChange(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.handleFiles(files);
    (event.target as HTMLInputElement).value = '';
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  syncEmails(): void {
    const emails = this.emailEntry
      .split(',')
      .map((e) => e.trim())
      .filter((e) => !!e);
    this.newTicket.emails = emails.length ? emails : this.newTicket.emails;
  }

  closeValidationModal(): void {
    this.showValidationModal = false;
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
  }

  private handleFiles(files: FileList | null): void {
    if (!files) {
      return;
    }

    Array.from(files).forEach((file) => {
      this.uploadedFiles.push({
        file,
        name: file.name,
        size: file.size,
        displaySize: this.formatFileSize(file.size)
      });
    });
  }

  private buildTickets(): SupportTicket[] {
    return [];
  }

  private buildEmailPreviewFromTicket(ticket: SupportTicket, fromEmail?: string): EmailPreviewData {
    const email = fromEmail || this.defaultEmail;
    return {
      fromEmail: email,
      subject: `[SUPPORT TICKET ${ticket.id}] ${ticket.subject}`,
      ticketId: ticket.id,
      submitter: `${this.displayName} (${this.accountLabel})`,
      userEmail: email,
      category: ticket.category,
      priority: ticket.priority,
      date: this.getFormattedDate(ticket.created),
      description: ticket.description
    };
  }

  private buildNewTicket(): TicketFormValues {
    return {
      subject: '',
      category: '',
      priority: 'medium',
      description: '',
      emails: [...this.buyerAccountEmails]
    };
  }

  private generateTicketId(): string {
    return 'MP-' + Math.floor(1000 + Math.random() * 9000);
  }

  private formatPriority(priority: string): SupportTicketPriority {
    switch (priority.toLowerCase()) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'urgent':
        return 'Urgent';
      default:
        return 'Medium';
    }
  }

  private getCategoryLabel(value: string): string {
    const categories: Record<string, string> = {
      catalog: 'Catalog Updates',
      product: 'Product Management',
      rfq: 'RFQ & Quoting',
      order: 'Purchase Orders',
      dispatch: 'Order Dispatch',
      invoice: 'Invoice Generation',
      search: 'Product Search',
      account: 'Account Issues',
      technical: 'Technical Problems',
      productSearch: 'Product Search',
      rfqCreation: 'RFQ Creation',
      supplierSelection: 'Supplier Selection & Management',
      quoteComparison: 'Quote Comparison & Evaluation',
      purchaseOrderManagement: 'Purchase Order Management',
      orderTracking: 'Order Tracking & Status',
      invoiceManagement: 'Invoice Management',
      accountProfile: 'Account & Profile',
      technicalIssues: 'Technical Issues',
      other: 'Other',
      catalogManagement: 'Catalog Management',
      productManagement: 'Product Management',
      rfqQuoting: 'RFQ & Quoting',
      purchaseOrders: 'Purchase Orders',
      orderDispatch: 'Order Dispatch & Tracking',
      invoiceGeneration: 'Invoice Generation',
      paymentFinance: 'Payment & Finance',
      accountProfileManagement: 'Account & Profile Management'
    };

    return categories[value] || value || 'General';
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private getFormattedDate(dateValue: string): string {
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) {
      return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  private async uploadAttachmentsToS3(): Promise<{ documentName: string; documentType: string; s3path: string }[]> {
    const uploads: { documentName: string; documentType: string; s3path: string }[] = [];

    for (const item of this.uploadedFiles) {
      const detail = {
        documentName: item.name,
        documentType: item.file.type || '',
        s3path: ''
      };

      const key = `support-ticket/buyer/${Date.now()}-${Math.random().toString(36).slice(2)}-${item.name}`;
      const s3Params = { fileKey: key, fileContentType: item.file.type || 'application/octet-stream' };

      try {
        const signedUrl = await this.awsSdkService.getSignedUrlPromise(s3Params);
        await this.webApiService.putRequestExternalApi(signedUrl, item.file);
        detail.s3path = this.awsSdkService.extractS3LocationFromUrl(signedUrl);
      } catch (err) {
        console.error('Failed to upload attachment to S3', item.name, err);
      }

      uploads.push(detail);
    }

    return uploads;
  }

  private async loadBuyerTickets(statusFilter?: string): Promise<void> {
    this.fullLoaderService.showLoader('Loading Tickets...');
    try {
      const response = await this.supportTicketService.listBuyerTickets({
        buyerId: this.buyerId || undefined,
        limit: 10,
        offset: 0,
        status: statusFilter || undefined
      });
      const rows = Array.isArray((response as any)?.rows)
        ? (response as any).rows
        : Array.isArray(response)
          ? response
          : [];

      this.tickets = rows.map((row: any) => this.mapApiTicket(row));
      this.selectedTicket = this.tickets.length ? this.tickets[0] : null;
      if (this.selectedTicket) {
        this.emailPreview = this.buildEmailPreviewFromTicket(this.selectedTicket, this.newTicket.emails[0]);
      }
      const totalElements = (response as any)?.totalElements ?? rows.length;
      this.updateStatsFromResponse(response, rows, totalElements);
      this.updateBuyerIdentityFromResponse(response);
    } catch (err) {
      console.error('Failed to load buyer support tickets', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  private mapApiTicket(row: any): SupportTicket {
    const createdDate = row?.createdAt ? new Date(row.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const priorityLabel = this.formatPriority(row?.priority || 'Medium');
    const attachments = this.parseAttachments(row?.attachments);
    const status = this.formatStatus(row?.status || 'Open');
    const emailsList = (row?.contactEmail || '')
      .split(',')
      .map((e: string) => e.trim())
      .filter(Boolean);
    const buyerDisplayName = row?.buyerCompanyName || row?.buyerName;

    return {
      id: row?.ticketRefNumber || row?.id || '',
      ticketRefNumber: row?.ticketRefNumber || row?.id,
      ticketId: row?.id,
      subject: row?.subject || 'Support Ticket',
      category: this.getCategoryLabel(row?.category),
      status: status,
      priority: priorityLabel,
      created: createdDate,
      submitter: `${buyerDisplayName || this.displayName} (${row?.contactEmail || ''}) - ${this.accountLabel}`,
      buyerName: buyerDisplayName,
      emails: emailsList,
      contactEmail: row?.contactEmail,
      description: row?.description || '',
      attachments
    };
  }

  private parseAttachments(raw: any): { name: string; size: string; icon: string }[] {
    if (!raw) return [];

    let parsed: any[] = [];
    try {
      if (typeof raw === 'string') {
        parsed = JSON.parse(raw);
      } else if (Array.isArray(raw)) {
        parsed = raw;
      }
    } catch {
      parsed = [];
    }

    return parsed
      .filter((doc) => !!doc)
      .map((doc) => ({
        name: doc.documentName || doc.name || 'Attachment',
        size: '',
        icon: 'fas fa-file',
        link: doc.documentFileS3Path || doc.s3path || doc.url || ''
      }));
  }

  private mapFilterToStatus(filter: string): string | undefined {
    switch (filter) {
      case 'open':
        return 'Open';
      case 'inProgress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return undefined;
    }
  }

  private formatStatus(status: string): SupportTicketStatus {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'open':
        return 'Open';
      case 'in progress':
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return 'Open';
    }
  }

  private updateStatsFromResponse(response: any, rows: any[], totalElements?: number): void {
    const total = typeof totalElements === 'number' ? totalElements : rows.length;
    const open = response?.openTotalCount ?? undefined;
    const inProgress = response?.inProgressTotalCount ?? undefined;
    const resolved = response?.resolvedTotalCount ?? undefined;
    const closed = response?.closedTotalCount ?? undefined;

    if (
      open !== undefined &&
      inProgress !== undefined &&
      resolved !== undefined &&
      closed !== undefined
    ) {
      this.stats.total = total;
      this.stats.open = open;
      this.stats.inProgress = inProgress;
      this.stats.resolved = resolved;
      return;
    }

    // fallback to computing from rows if counts missing
    const counts = rows.reduce(
      (acc, row) => {
        const status = (row?.status || '').toLowerCase();
        if (status === 'open') acc.open += 1;
        if (status === 'in progress') acc.inProgress += 1;
        if (status === 'resolved') acc.resolved += 1;
        if (status === 'closed') acc.closed += 1;
        return acc;
      },
      { open: 0, inProgress: 0, resolved: 0, closed: 0 }
    );
    this.stats.total = total;
    this.stats.open = counts.open;
    this.stats.inProgress = counts.inProgress;
    this.stats.resolved = counts.resolved;
  }

  async exportTickets(): Promise<void> {
    try {
      this.fullLoaderService.showLoader('Exporting tickets...');
      const XLSX = await import('xlsx');

      const exportData = this.tickets.map((ticket, index) => {
        const attachmentsLinks = (ticket.attachments || [])
          .map((a) => a.link || a.name)
          .filter(Boolean)
          .join(', ');

        return {
          'Sr.No': index + 1,
          'Ticket Ref Number': ticket.id,
          Buyer_Name: ticket.buyerName || '',
          Emails: (ticket.emails && ticket.emails.length ? ticket.emails : ticket.contactEmail ? [ticket.contactEmail] : []).join(', '),
          Subject: ticket.subject || '',
          Description: ticket.description || '',
          Category: ticket.category || '',
          Status: ticket.status || '',
          Priority: ticket.priority || '',
          Attachments_Links: attachmentsLinks,
          Created_On: ticket.created || ''
        };
      });

      const statusLabel = this.mapFilterToStatus(this.filterStatus) || 'All_Tickets';
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const fileName = `Support_Ticket_System_${statusLabel.replace(/\\s+/g, '_')}_${formattedDate}.xlsx`;

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Failed to export tickets', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  private updateBuyerIdentityFromResponse(response: any): void {
    const details = response?.buyerDetails || response?.buyerDetail || response?.buyerInfo || response?.buyer;
    const firstRow = Array.isArray(response?.rows) && response.rows.length ? response.rows[0] : null;

    const resolvedName =
      details?.buyerCompanyName ||
      details?.companyName ||
      details?.buyerName ||
      details?.name ||
      firstRow?.buyerCompanyName ||
      firstRow?.buyerName ||
      '';

    if (resolvedName) {
      this.buyerAccountName = resolvedName;
      this.buyerName = resolvedName;
    }

    const resolvedEmails = this.normalizeEmails(
      details?.buyerEmail ||
        details?.email ||
        details?.contactEmail ||
        details?.contactEmails ||
        firstRow?.contactEmail
    );

    if (resolvedEmails.length) {
      this.buyerAccountEmails = resolvedEmails;
      if (!this.emailEntry.trim() || !this.newTicket.emails.length) {
        this.newTicket.emails = [...resolvedEmails];
        this.emailEntry = resolvedEmails.join(', ');
      }
    }
  }

  private normalizeEmails(emails: string | string[] | null | undefined): string[] {
    if (!emails) return [];
    if (Array.isArray(emails)) {
      return emails.map((e) => e.trim()).filter(Boolean);
    }
    return emails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
  }

  private getStatusUpdateOptions(status: SupportTicketStatus): SupportTicketStatus[] {
    if (status === 'Resolved') return ['Closed'];
    return [];
  }

  private validateStatus(status: string): SupportTicketStatus | null {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'open') return 'Open';
    if (normalized === 'in progress' || normalized === 'in_progress') return 'In Progress';
    if (normalized === 'resolved') return 'Resolved';
    if (normalized === 'closed') return 'Closed';
    return null;
  }
}
